import React from "react";
import { APActivity } from "@/apps/tatar/activityApp/ActivityInterfaces";
import { RentalUnit } from "@/apps/tatar/cashBudget/views/tenants/TenantsInterfaces";
import { OAObject } from "@/apps/tatar/objectsApp/types/object.interface";
import AssetLoader from "@/components/AssetLoader/AssetLoader";
import { AssetTypes } from "@/model/AssetTypes";
import OrgaStruct from "@/redux/actions/struct/implemented/OrgaStruct";
import PromiseLoader from "@/components/PromiseLoader/PromiseLoader";
import { HTTP } from "@/utils/Http";
import MQ from "@/utils/MatchQueryUtils";
import StructLoader from "@/components/StructLoader/StructLoader";
import ObjectKindStruct from "@/redux/actions/struct/implemented/ObjectKindStruct";
import i18n from "@/i18n";
import { ActivityStructContext } from "@/apps/tatar/activityApp/ActivityStructContext";
import BFButton from "@/modules/abstract-ui/general/Button/BFButton";
import DataBusDefaults from "@/services/DataBusDefaults";

type Props = {
  activity: APActivity;
  children: (
    rentalUnit: RentalUnit,
    object: OAObject,
    entity: any
  ) => React.ReactNode;
};

const VacancyContractDataLoader = (props: Props) => {
  const { activity, children } = props;

  const rentalUnitRelations = activity.data.relations.filter(
    (r) => r.assetType === AssetTypes.Rental.RentalUnit
  );

  const entity = OrgaStruct.getEntity(activity.data.entity);
  const entityObject = entity.objects.find(
    (o) => o._id === activity.data.objectId
  );

  return (
    <StructLoader
      structTypes={["objectKind", "vacancy"]}
      unitType={activity.data.type}
      render={([objectKindStruct, vacancyStruct]) => {
        return (
          <ActivityStructContext.Provider value={vacancyStruct}>
            <AssetLoader
              id={entityObject._id}
              assetType={AssetTypes.Portfolio.Object}
              render={(object: OAObject) => (
                <PromiseLoader
                  promise={async () => {
                    const matchQuery = MQ.and(
                      MQ.in(
                        "_id",
                        rentalUnitRelations.map((r) => r.assetId)
                      )
                    );
                    const result = await HTTP.post({
                      url: `/asset/list/${AssetTypes.Rental.RentalUnit}`,
                      bodyParams: {
                        matchQuery,
                      },
                    });

                    return result.data as RentalUnit[];
                  }}
                  render={(rentalUnits: RentalUnit[]) => {
                    const residentalUnits = rentalUnits.filter((r) => {
                      const rentalUnitType = ObjectKindStruct.getUnitTypeBy(
                        r.data.unitType
                      );
                      return rentalUnitType.group === "residental";
                    });

                    const rentalUnit =
                      residentalUnits.find((r) => {
                        return (
                          r.data.building !== null &&
                          r.data.building !== undefined
                        );
                      }) || residentalUnits[0];

                    if (!rentalUnit) {
                      return (
                        <div className="padding-20 __card">
                          <div className="__h3">
                            {i18n.t(
                              "apTemplate:Vacancy.Applicant.ContractDetails.Label.NoRentalUnit",
                              "Es wurde keine Mieteinheit gefunden."
                            )}
                            <div className="__sub-text margin-top-20">
                              {i18n.t(
                                "apTemplate:Vacancy.Applicant.ContractDetails.Label.NoRentalUnit.Description",
                                "Bitte prüfen Sie ob die Mieteinheit im Leerstand nicht gewerblich ist."
                              )}
                            </div>
                            <BFButton
                              appearance="link"
                              onClick={() => {
                                DataBusDefaults.closeAllModals();
                              }}
                            >
                              {i18n.t("Globals.Buttons.Close", "Schließen")}
                            </BFButton>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <AssetLoader
                        id={rentalUnit._id}
                        assetType={AssetTypes.Rental.RentalUnit}
                        render={(rentalUnit: RentalUnit) => {
                          return <>{children(rentalUnit, object, entity)}</>;
                        }}
                      />
                    );
                  }}
                />
              )}
            />
          </ActivityStructContext.Provider>
        );
      }}
    />
  );
};

export default VacancyContractDataLoader;
